import { createContext, useContext, useEffect, useReducer } from 'react';
import ajaxUser from '../util/remote/ajaxUser';
import ajaxConfig from '../util/remote/ajaxConfig';

const initialState = { 
  user: null,
  connectedProfiles: null,
  activeProfile: null,
  myPermissions: null,
  centralUser: null,
  licenseInfo: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'CLEAR':
      return { ...initialState };  // Reset state completely
    default:
      return { ...state, [action.type]: action.payload };
  }
};

let tracking = false;

const AuthContext = createContext();

export const AuthProvider = ({children}) =>{
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    // Only track when profile is active
    if (!state.activeProfile) return;

    // Load license when profile loads
    getLicenseInfo();

    // Call once immediately
    trackOnline();

    // Then every 60 seconds
    const intervalId = setInterval(() => {
      trackOnline();
    }, 60 * 1000);

    // Cleanup when activeProfile changes or provider unmounts
    return () => {
      clearInterval(intervalId);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.activeProfile]);



  const getMyProfile = async (refresh = false) => {
    if(!refresh && state.user) return;
    const server_response  = await ajaxUser.fetchMyProfile();
    if(server_response.status === "OK"){
      dispatch({ type: 'user', payload: server_response.details });
    }
  };

  const getProfiles = async(refresh)=>{
    if(refresh){
      dispatch({type: 'connectedProfiles', payload: null});
    }
    const server_response = await ajaxUser.fetchUserProfiles();
    if(server_response.status === "OK"){
      dispatch({
        type: 'activeProfile',
        payload: server_response.details.action === 'select' ? false : true
      });
      if(!server_response.details.data){
        dispatch({type: 'connectedProfiles', payload: '404'});
      }else{
        dispatch({type: 'connectedProfiles', payload: server_response.details.data});
      }
    }else{
      dispatch({type: 'connectedProfiles', payload: '404'});
    }
  };

  const getCentralUser = async (refresh = false) => {
    if(!refresh && state.centralUser) return;
    const server_response  = await ajaxUser.fetchCentralProfile();
    if(server_response.status === "OK"){
      dispatch({ type: 'centralUser', payload: server_response.details });
    }
  };

  const getLicenseInfo = async (refresh = false) => {
    if(!state.activeProfile) return;
    if(!refresh && state.licenseInfo) return;

    const orgId = state.connectedProfiles?.org_id;
    if (!orgId) {
      dispatch({ type: 'licenseInfo', payload: '404' });
      return;
    }

    const server_response  = await ajaxConfig.fetchLicenseInfo({organisation_id: orgId});

    if(server_response.status === "OK"){
      let details = server_response.details || {};

      // 🧠 Derive unpaidInvoice (earliest pending invoice, if any)
      const invoices = Array.isArray(details.invoices) ? details.invoices : [];
      let unpaidInvoice = null;

      if (invoices.length > 0) {
        const pending = invoices
          .filter(inv => (inv.status || '').toLowerCase() === 'pending')
          .sort((a, b) => {
            const aStart = new Date(a.start || a.billing_period_start || 0);
            const bStart = new Date(b.start || b.billing_period_start || 0);
            return aStart - bStart;
          });

        unpaidInvoice = pending.length > 0 ? pending[0] : null;
      }

      details = {
        ...details,
        unpaidInvoice,
      };

      dispatch({ type: 'licenseInfo', payload: details });
    }else{
      dispatch({ type: 'licenseInfo', payload: '404' });
    }
  };

  const trackOnline = async () => {
    if (tracking) return;
    tracking = true;
    try {
      await ajaxUser.trackOnline();
    } finally {
      tracking = false;
    }
  };

  const unpaidInvoice =
    state.licenseInfo && state.licenseInfo !== '404'
      ? state.licenseInfo.unpaidInvoice || null
      : null;

  return(
    <AuthContext.Provider value={{
      ...state, 
      unpaidInvoice,
      dispatch, 
      getMyProfile,
      getProfiles,
      getCentralUser,
      getLicenseInfo,
    }}>
      {children}
    </AuthContext.Provider>
  );

};

export const useAuth = ()=>useContext(AuthContext);
